/**
 * ExitIntelTab — property workspace tab for BP-029.
 *
 * Compact 8-strategy exit matrix inline on the property workspace.
 * Renders the recommended exit + a mini ranked comparison.
 * Deep-links to `/v3/intel/exit-intel` for the full analysis.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Home, Building, Hammer, Handshake, KeyRound, Wallet,
  RefreshCcw, Layers, Loader2, Sparkles, Award, ArrowUpRight,
  DollarSign, Timer, Percent, ShieldCheck, TrendingUp,
} from "lucide-react";
import { ExitTimelineBar } from "@/components/v3/PropertyCharts";

const STRATEGY_META = {
  wholesale:      { label: "Wholesale",      icon: Handshake,  accent: "#0EA5E9" },
  wholetail:      { label: "Wholetail",      icon: RefreshCcw, accent: "#06B6D4" },
  fix_flip:       { label: "Fix & Flip",     icon: Hammer,     accent: "#EA580C" },
  rental:         { label: "Rental",         icon: Home,       accent: "#10B981" },
  brrrr:          { label: "BRRRR",          icon: Building,   accent: "#8B5CF6" },
  seller_finance: { label: "Seller Finance", icon: Wallet,     accent: "#F59E0B" },
  novation:       { label: "Novation",       icon: KeyRound,   accent: "#D946EF" },
  hold:           { label: "Hold",           icon: Layers,     accent: "#64748B" },
};

const RISK_META = {
  low:      { label: "Low",      color: "text-emerald-600" },
  moderate: { label: "Moderate", color: "text-amber-600" },
  elevated: { label: "Elevated", color: "text-orange-600" },
  high:     { label: "High",     color: "text-red-600" },
};

const fmtMoney = (n) => new Intl.NumberFormat("en-US",
  { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;

export default function ExitIntelTab({ property }) {
  const propertyId = property?.id;
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/exit-intel/${propertyId}`);
      setMatrix(data);
    } catch { setMatrix(null); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const analyze = useCallback(async () => {
    if (!propertyId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/v3/exit-intel/analyze",
        { property_id: propertyId, force: true });
      setMatrix(data);
      toast.success(`Analyzed ${data.strategies?.length ?? 0} exit strategies.`);
    } catch { toast.error("Analysis failed."); }
    finally { setBusy(false); }
  }, [propertyId]);

  if (loading) {
    return <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  if (!matrix) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center"
           data-testid="exit-intel-tab-empty">
        <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto" />
        <h3 className="mt-3 text-base font-bold text-slate-900">No exit intelligence yet</h3>
        <p className="mt-1 text-sm text-slate-500">Run all 8 strategies against this property.</p>
        <button type="button" onClick={analyze} disabled={busy}
                data-testid="exit-intel-tab-analyze"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 disabled:opacity-60"
                style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Analyze Exits
        </button>
      </div>
    );
  }

  const rec = matrix.strategies.find((s) => s.strategy === matrix.recommended);
  const RecIcon = STRATEGY_META[rec?.strategy]?.icon || Home;
  const accent = STRATEGY_META[rec?.strategy]?.accent || "#10B981";

  return (
    <div className="space-y-4" data-testid="exit-intel-tab">
      {/* Recommended */}
      {rec && (
        <div className="rounded-xl border-2 border-emerald-300/60 bg-gradient-to-br from-emerald-50 to-white p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full text-[9px] font-black tracking-widest uppercase px-2 py-0.5 text-white"
                  style={{ backgroundImage: "linear-gradient(90deg, #10B981, #059669)" }}>
              <Award className="w-3 h-3" /> Recommended Exit
            </span>
            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">
              {matrix.ai_source === "claude" ? "Claude" : "Deterministic"}
            </span>
          </div>
          <div className="mt-2 flex items-start gap-3 flex-wrap">
            <div className="grid place-items-center h-11 w-11 rounded-lg text-white shadow"
                 style={{ background: `linear-gradient(135deg, ${accent}, #059669)` }}>
              <RecIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Exit strategy</div>
              <div className="text-xl font-bold text-slate-900 leading-tight">{rec.label}</div>
              <p className="mt-1 text-[12px] text-slate-600 leading-snug">{matrix.recommendation_reason}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
            <MiniStat icon={DollarSign} label="Profit"       value={fmtMoney(rec.math.total_profit)} highlight />
            <MiniStat icon={Percent}    label="Annualized"   value={fmtPct(rec.math.annualized_roi)} />
            <MiniStat icon={Timer}      label="Timeline"     value={`${rec.math.timeline_days}d`} />
            <MiniStat icon={DollarSign} label="Cash at close" value={fmtMoney(rec.math.cash_at_close)} />
          </div>
          {matrix.ai_summary && (
            <div className="mt-3 rounded-lg bg-white border border-emerald-200 p-3 text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-line">
              {matrix.ai_summary}
            </div>
          )}
        </div>
      )}

      {/* Portfolio impact chip strip */}
      {matrix.portfolio_impact?.concentration_note && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap">
          <Building className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Portfolio impact:</span>
          <span className="text-[12px] text-slate-700">{matrix.portfolio_impact.concentration_note}</span>
        </div>
      )}

      {/* Timeline bar chart — visualizes days-to-exit for each strategy */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="exit-timeline-chart">
        <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600 mb-2">Exit Timelines</div>
        <ExitTimelineBar strategies={matrix.strategies} recommended={matrix.recommended} />
      </div>

      {/* Mini ranked table */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">All exits — ranked</div>
        <div className="mt-3 overflow-hidden rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Strategy</th>
                <th className="text-right px-3 py-2">Profit</th>
                <th className="text-right px-3 py-2">Annual ROI</th>
                <th className="text-right px-3 py-2">Timeline</th>
                <th className="text-left px-3 py-2">Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matrix.strategies.map((s) => {
                const Icon = STRATEGY_META[s.strategy]?.icon || Home;
                const risk = RISK_META[s.risk_band] || RISK_META.moderate;
                const isRec = s.strategy === matrix.recommended;
                return (
                  <tr key={s.strategy} className={isRec ? "bg-emerald-50/40" : ""}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-800">
                        <Icon className="w-3.5 h-3.5" style={{ color: STRATEGY_META[s.strategy]?.accent }} />
                        {s.label}
                        {isRec && <Award className="w-3 h-3 text-emerald-500" />}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] font-bold text-slate-900">{fmtMoney(s.math.total_profit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] text-slate-700">{fmtPct(s.math.annualized_roi)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] text-slate-700">{s.math.timeline_days}d</td>
                    <td className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${risk.color}`}>
                      <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />{risk.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-100">
        <button type="button" onClick={analyze} disabled={busy}
                data-testid="exit-intel-tab-reanalyze"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          Re-analyze
        </button>
        <Link to="/v3/intel/exit-intel"
              data-testid="exit-intel-tab-deeplink"
              className="inline-flex items-center gap-1.5 rounded-lg text-emerald-700 text-xs font-bold px-3 py-1.5 hover:bg-emerald-50">
          Open Exit Strategy Intelligence workspace <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg px-2.5 py-1.5 border ${highlight ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}>
      <div className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-0.5 font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
